// Вкладка «Вес»: последний вес и среднее за 7 дней, график с линией среднего, записи, импорт Zepp Life.

import { useState } from 'react'

import { TimeSeriesChart, type ChartSeries } from '@/components/charts/TimeSeriesChart'
import { Button, Card, ErrorText, Spinner, Stat } from '@/components/ui'
import { formatDate, formatNumber, formatSigned } from '@/lib/format'
import { todayIso, type Period } from '@/lib/periods'
import { useCalibration } from '@/pages/inbody/api'
import { offsetFor } from '@/pages/inbody/targets/logic'
import { useZeppSamples } from '@/pages/inbody/targets/zepp'
import { ZeppToggle } from '@/pages/inbody/targets/ZeppToggle'
import { useMetricTypes, useSeries, type MetricType } from '@/pages/metrics/api'
import { averagePoints, dayPoints, seriesQuery } from '@/pages/metrics/series'

import { ImportDialog } from './ImportDialog'
import { EmptyCard, SampleDialog, SamplesList } from './parts'

const TREND_DAYS = 7

const FALLBACK_WEIGHT: MetricType = {
  key: 'weight',
  label_ru: 'Вес',
  unit: 'кг',
  category: 'body',
  min_value: 20,
  max_value: 400,
  is_custom: false,
}

export function WeightPage() {
  const types = useMetricTypes()
  const weight = types.data?.find((t) => t.key === 'weight') ?? FALLBACK_WEIGHT
  const [period, setPeriod] = useState<Period>('month')
  const [adding, setAdding] = useState(false)
  const [importing, setImporting] = useState(false)
  const today = todayIso()
  const series = useSeries('weight', seriesQuery(period, today, TREND_DAYS))
  // «Zepp с поправкой»: сдвигаем только на экране, значения в базе не меняются.
  const [adjusted, setAdjusted] = useState(false)
  const calibration = useCalibration()
  const zeppSamples = useZeppSamples('weight')
  const offset = offsetFor(calibration.data, 'weight')

  const actions = (
    <>
      <Button variant="primary" icon="plus" onClick={() => setAdding(true)}>
        Добавить вес
      </Button>
      <Button icon="download" onClick={() => setImporting(true)}>
        Импортировать из Zepp Life
      </Button>
    </>
  )

  let body
  if (series.isLoading) {
    body = (
      <div className="flex justify-center py-16">
        <Spinner />
      </div>
    )
  } else if (series.error) {
    body = <ErrorText error={series.error} />
  } else if (!series.data?.latest) {
    body = (
      <Card>
        <EmptyCard title="Пока нет взвешиваний — добавь первое или импортируй из Zepp Life">{actions}</EmptyCard>
      </Card>
    )
  } else {
    const data = series.data
    const latest = data.latest!
    const points = data.points
    const firstInPeriod = points[0]?.value ?? null
    const change = firstInPeriod !== null && points.length > 1 ? latest.value - firstInPeriod : null
    // Поправка — только для дневного ряда: в недельном среднем источники смешаны.
    const canAdjust = offset !== null && data.bucket === 'day'
    const shift = adjusted && canAdjust ? offset : null
    // Точка дня — первая запись дня; сдвигаем её, только если это запись Zepp (сверка по времени).
    const zeppTimes = new Set((zeppSamples.data ?? []).map((s) => Date.parse(s.measured_at)))
    const zeppDays = new Set(points.filter((p) => p.at !== null && zeppTimes.has(Date.parse(p.at))).map((p) => p.day))
    const shifted = <T extends { x: string; y: number | null }>(list: T[]): T[] =>
      shift === null
        ? list
        : list.map((p) => (p.y !== null && zeppDays.has(p.x) ? { ...p, y: Math.round((p.y - shift) * 100) / 100 } : p))
    const chart: ChartSeries[] = [
      {
        key: 'weight',
        label: shift === null ? 'Вес' : 'Вес (Zepp с поправкой)',
        unit: 'кг',
        points: shifted(dayPoints(points)),
        digits: 1,
      },
    ]
    if (data.bucket === 'day') {
      chart.push({ key: 'avg', label: `Среднее за ${TREND_DAYS} дней`, unit: 'кг', axis: 'left', points: shifted(averagePoints(points)), digits: 1 })
    }
    body = (
      <div className="flex flex-col gap-4">
        <Card>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
            <Stat
              label="Последний"
              value={formatNumber(latest.value, { digits: 1, fixed: true })}
              unit="кг"
              hint={formatDate(latest.day)}
            />
            <Stat
              label={`Среднее за ${TREND_DAYS} дней`}
              value={formatNumber(data.trend, { digits: 1, fixed: true })}
              unit={data.trend !== null ? 'кг' : undefined}
              hint="по первому взвешиванию дня"
            />
            <Stat
              label="За период"
              value={formatSigned(change, { digits: 1 })}
              unit={change !== null ? 'кг' : undefined}
              className="hidden sm:block"
            />
          </div>
        </Card>
        <Card>
          {canAdjust ? (
            <div className="mb-2">
              <ZeppToggle checked={adjusted} onChange={setAdjusted} />
              <p className="text-xs text-muted">
                {Array.isArray(calibration.data) ? calibration.data.find((c) => c.metric === 'weight')?.message_ru : null}
              </p>
            </div>
          ) : null}
          <TimeSeriesChart series={chart} period={period} onPeriodChange={setPeriod} today={today} height={260} />
          {data.bucket === 'week' ? (
            <p className="mt-2 text-xs text-muted">Период больше года — точки показывают среднее за неделю.</p>
          ) : null}
        </Card>
        <Card title="Взвешивания">
          <SamplesList type={weight} emptyText="Записей нет" />
        </Card>
      </div>
    )
  }

  return (
    <>
      {series.data?.latest ? <div className="mb-4 flex flex-wrap gap-2">{actions}</div> : null}
      {body}
      {adding ? <SampleDialog open onClose={() => setAdding(false)} type={weight} /> : null}
      <ImportDialog open={importing} onClose={() => setImporting(false)} />
    </>
  )
}
